import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { controlFocus } from '../primitives/shared.js';

export interface AppShellProps {
  /** Rendered above the navigation chrome — offline and impersonation banners. */
  banners?: ReactNode;
  header?: ReactNode;
  nav?: ReactNode;
  footer?: ReactNode;
  /** Slot for dialogs, drawers and the toast region, kept out of `main`. */
  overlays?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Page shell owned by the design system (07-11 §Scope and Authority).
 * Provides the landmark structure and the skip link that 07-10 requires as the
 * first focusable element on every page.
 */
export function AppShell({
  banners,
  header,
  nav,
  footer,
  overlays,
  children,
  className,
}: AppShellProps) {
  return (
    <div data-layout="app-shell" className={cn('flex min-h-screen flex-col bg-page', className)}>
      <a
        href="#main"
        className={cn(
          'sr-only focus:not-sr-only focus:absolute focus:z-[var(--z-impersonation)] focus:m-xs focus:rounded-control focus:bg-raised focus:px-field focus:py-inline focus:text-body focus:font-medium',
          controlFocus,
        )}
      >
        Skip to main content
      </a>
      {banners}
      {header}
      <div className="flex flex-1 flex-col md:flex-row">
        {nav}
        <main id="main" tabIndex={-1} className="min-w-0 flex-1 px-gutter py-section">
          {children}
        </main>
      </div>
      {footer}
      {overlays}
    </div>
  );
}
